import { useEffect, useMemo, useState } from "react";
import {
  DIMENSION_LABELS,
  RANGE_LABELS,
  REPORT_MEASURES,
  defaultReportSpec,
  flattenLocations,
  formatCount,
  formatMoney,
  filterChoices,
  measureLabel,
  normaliseReportSpec,
  pathLabel,
  reportCsv,
  reportDimensions,
  reportInsights,
  reportMeasures,
  resolveRange,
  runReport,
  todayLocal,
  type InventoryKind,
  type InsightLine,
  type ReportDimension,
  type ReportFacts,
  type ReportLabels,
  type ReportMeasure,
  type ReportRangePreset,
  type ReportResult,
  type ReportSpec,
  type Reserve,
  type SavedReport,
} from "@iltocat/shared";
import { ApiError, api, plural } from "../lib/api.js";
import { downloadBlob } from "../lib/download.js";
import { calendarDay } from "../lib/stockFormat.js";
import type { Taxonomy } from "../lib/useTaxonomy.js";
import type { Vocabulary } from "../lib/useVocabulary.js";
import { FilterChip, FilterToggle, type FilterOption } from "./FilterChip.js";
import { Icon } from "./Icon.js";
import { Notice } from "./Notice.js";
import { Panel } from "./Panel.js";
import { Segmented } from "@vcnet/ui";
import { useToast } from "./Toast.js";

/**
 * Stage 6's reports you build, and the insights read from the same rows.
 *
 * One fetch of facts (`/api/reports/facts`) feeds both; every choice after
 * that -- grouping, totals, filters, sort -- reruns `runReport` on the spot.
 * The facts reach back over the chosen date range and never less than 90
 * days, which is what the insights need. A new range refetches.
 *
 * Saved reports are the inventory's, so everybody in it sees the same list; a
 * viewer opens them but does not save or delete (`canEdit`). The report on
 * screen is also remembered per device, so the screen reopens where it was.
 */

const LAST_KEY = (kind: InventoryKind) => `iltocat.report.builder.${kind}`;

function readLast(kind: InventoryKind): ReportSpec {
  try {
    const raw = window.localStorage.getItem(LAST_KEY(kind));
    return normaliseReportSpec(raw ? JSON.parse(raw) : null, kind);
  } catch {
    return defaultReportSpec(kind);
  }
}

function saveLast(kind: InventoryKind, spec: ReportSpec): void {
  try {
    window.localStorage.setItem(LAST_KEY(kind), JSON.stringify(spec));
  } catch {
    // A private window: the screen opens on the default next time, which is fine.
  }
}

const addDays = (day: string, days: number): string => {
  const [y = 1970, m = 1, d = 1] = day.split("-").map(Number);
  return todayLocal(new Date(y, m - 1, d + days, 12));
};

/** What a filter chip can narrow to, for a dimension the builder filters on. */
const FILTERABLE: ReportDimension[] = [
  "place",
  "status",
  "kind",
  "client",
  "category",
  "location",
  "tag",
  "vendor",
  "tracking",
  "condition",
];

export function BuiltReports({
  kind,
  canEdit,
  taxonomy,
  vocabulary,
  onOpen,
}: {
  kind: InventoryKind;
  canEdit: boolean;
  taxonomy: Taxonomy;
  vocabulary: Vocabulary;
  onOpen: (itemId: string) => void;
}) {
  const today = todayLocal();
  const [spec, setSpecState] = useState<ReportSpec>(() => readLast(kind));
  const [facts, setFacts] = useState<ReportFacts | null>(null);
  const [saved, setSaved] = useState<SavedReport[]>([]);
  const [short, setShort] = useState<Reserve[]>([]);
  const [error, setError] = useState<string | null>(null);

  const setSpec = (next: ReportSpec) => {
    setSpecState(next);
    saveLast(kind, next);
  };

  // The window of movements to fetch: the report's range, stretched to cover
  // the last 90 days for the insights.
  const range = spec.source === "activity" ? resolveRange(spec.range, today) : null;
  const ninety = addDays(today, -89);
  const from = range && range.from < ninety ? range.from : ninety;
  const to = range && range.to > today ? range.to : today;

  useEffect(() => {
    let live = true;
    api.reports
      .facts(from, to)
      .then(({ facts: next }) => live && setFacts(next))
      .catch((problem: unknown) => {
        if (live) setError(problem instanceof ApiError ? problem.message : "Could not load the report.");
      });
    return () => {
      live = false;
    };
  }, [from, to]);

  useEffect(() => {
    void api.reports
      .list()
      .then(({ reports }) => setSaved(reports))
      .catch(() => undefined);
    if (kind === "stock") {
      void api.reserves
        .list()
        .then(({ reserves }) => setShort(reserves.filter((reserve) => reserve.status === "short")))
        .catch(() => undefined);
    }
  }, [kind]);

  const locationLabels = useMemo(
    () => new Map(flattenLocations(taxonomy.tree).map((node) => [node.id, pathLabel(node.path)])),
    [taxonomy.tree],
  );
  const labels: ReportLabels = {
    categories: new Map(taxonomy.categories.map((one) => [one.id, one.name])),
    locations: locationLabels,
    tags: new Map(vocabulary.tags.map((tag) => [tag.id, tag.name])),
    clients: new Map((facts?.clients ?? []).map((client) => [client.id, client.name])),
    parentOf: (id) => taxonomy.byId.get(id)?.parentId ?? null,
  };

  const result = facts ? runReport(spec, facts, labels, today) : null;
  const insights = facts ? reportInsights(facts, today) : null;

  return (
    <>
      {error && <Notice tone="error">{error}</Notice>}
      <InsightsPanel kind={kind} insights={insights} short={short} onOpen={onOpen} />
      <Panel
        title="Build a report"
        note={
          kind === "stock"
            ? "Group what you hold, or what happened over a stretch of time, and total it. Saved reports are shared with everybody in this inventory."
            : "Group what you hold and total it. Saved reports are shared with everybody in this inventory."
        }
      >
        <SavedReports
          saved={saved}
          canEdit={canEdit}
          spec={spec}
          onOpenReport={(one) => setSpec(normaliseReportSpec(one.spec, kind))}
          onSaved={setSaved}
          onError={setError}
        />
        <Controls kind={kind} spec={spec} facts={facts} labels={labels} today={today} onChange={setSpec} />
        {result === null ? (
          <div className="skeleton-line" />
        ) : (
          <ResultTable
            result={result}
            spec={spec}
            onSort={(sort) => setSpec({ ...spec, sort })}
            onOpen={onOpen}
          />
        )}
      </Panel>
    </>
  );
}

/* ---------------------------------------------------------------- saved -- */

function SavedReports({
  saved,
  canEdit,
  spec,
  onOpenReport,
  onSaved,
  onError,
}: {
  saved: SavedReport[];
  canEdit: boolean;
  spec: ReportSpec;
  onOpenReport: (report: SavedReport) => void;
  onSaved: (reports: SavedReport[]) => void;
  onError: (message: string | null) => void;
}) {
  const [naming, setNaming] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  async function save() {
    if (naming === null || !naming.trim()) return;
    setBusy(true);
    onError(null);
    try {
      const { reports } = await api.reports.save(naming.trim(), spec);
      onSaved(reports);
      setNaming(null);
      toast("Saved for everybody in this inventory.");
    } catch (problem) {
      onError(problem instanceof ApiError ? problem.message : "That report did not save.");
    } finally {
      setBusy(false);
    }
  }

  async function forget(report: SavedReport) {
    onError(null);
    try {
      onSaved((await api.reports.remove(report.id)).reports);
      toast(`Deleted “${report.name}”.`);
    } catch (problem) {
      onError(problem instanceof ApiError ? problem.message : "That did not delete.");
    }
  }

  if (saved.length === 0 && !canEdit) return null;
  return (
    <div className="chip-bar report-saved" role="group" aria-label="Saved reports">
      {saved.map((report) => (
        <span key={report.id} className="chip chip-saved">
          <button
            type="button"
            className="chip-main"
            title={`Saved by ${report.createdBy || "somebody"}`}
            onClick={() => onOpenReport(report)}
          >
            <Icon name="chart" size={14} />
            {report.name}
          </button>
          {canEdit && (
            <button
              type="button"
              className="chip-x"
              aria-label={`Delete saved report ${report.name}`}
              onClick={() => void forget(report)}
            >
              <Icon name="x" size={14} />
            </button>
          )}
        </span>
      ))}
      {canEdit &&
        (naming === null ? (
          <button type="button" className="chip chip-ghost" onClick={() => setNaming("")}>
            <Icon name="plus" size={14} />
            Save this report
          </button>
        ) : (
          <form
            className="chip-form"
            onSubmit={(event) => {
              event.preventDefault();
              void save();
            }}
          >
            <input
              className="input input-small"
              aria-label="Name for this report"
              placeholder="Name it, like “Out at clients”"
              value={naming}
              // Typed straight after pressing Save.
              autoFocus
              onChange={(event) => setNaming(event.target.value)}
            />
            <button type="submit" className="button button-small" disabled={busy || !naming.trim()}>
              Save
            </button>
            <button
              type="button"
              className="button button-quiet button-small"
              onClick={() => setNaming(null)}
            >
              Cancel
            </button>
          </form>
        ))}
    </div>
  );
}

/* ------------------------------------------------------------- controls -- */

function Controls({
  kind,
  spec,
  facts,
  labels,
  today,
  onChange,
}: {
  kind: InventoryKind;
  spec: ReportSpec;
  facts: ReportFacts | null;
  labels: ReportLabels;
  today: string;
  onChange: (spec: ReportSpec) => void;
}) {
  const dimensions = reportDimensions(spec.source, kind);
  const range = resolveRange(spec.range, today);

  const optionsFor = (dim: ReportDimension): FilterOption<string>[] =>
    facts === null ? [] : filterChoices(spec, facts, dim, labels, today);

  const switchSource = (source: ReportSpec["source"]) =>
    onChange({
      ...spec,
      source,
      groupBy: source === "activity" ? "month" : "category",
      thenBy: null,
      filters: source === "activity" ? { kind: "deployed" } : {},
      measures: source === "activity" ? ["units", "value"] : defaultReportSpec(kind).measures,
      sort: { by: "label", dir: "asc" },
    });

  const filters = FILTERABLE.filter((dim) => dimensions.includes(dim));
  const anyFilter = Object.values(spec.filters).some((value) => value !== undefined);

  return (
    <div className="report-controls">
      {kind === "stock" && (
        <Segmented<ReportSpec["source"]>
          label="What to report on"
          value={spec.source}
          onChange={switchSource}
          options={[
            { value: "holdings", label: "What you hold" },
            { value: "activity", label: "What happened" },
          ]}
        />
      )}

      <div className="report-row">
        <label className="field report-field">
          <span className="field-label">Group by</span>
          <select
            className="input"
            value={spec.groupBy}
            onChange={(event) => {
              const groupBy = event.target.value as ReportDimension;
              onChange({ ...spec, groupBy, thenBy: spec.thenBy === groupBy ? null : spec.thenBy });
            }}
          >
            {dimensions.map((dim) => (
              <option key={dim} value={dim}>
                {DIMENSION_LABELS[dim]}
              </option>
            ))}
          </select>
        </label>
        <label className="field report-field">
          <span className="field-label">Then by</span>
          <select
            className="input"
            value={spec.thenBy ?? ""}
            onChange={(event) =>
              onChange({
                ...spec,
                thenBy: event.target.value ? (event.target.value as ReportDimension) : null,
              })
            }
          >
            <option value="">Nothing</option>
            {dimensions
              .filter((dim) => dim !== spec.groupBy)
              .map((dim) => (
                <option key={dim} value={dim}>
                  {DIMENSION_LABELS[dim]}
                </option>
              ))}
          </select>
        </label>
        {spec.source === "activity" && (
          <label className="field report-field">
            <span className="field-label">When</span>
            <select
              className="input"
              value={spec.range.preset}
              onChange={(event) => {
                const preset = event.target.value as ReportRangePreset;
                onChange({
                  ...spec,
                  range: preset === "custom" ? { preset, from: range.from, to: range.to } : { preset },
                });
              }}
            >
              {(Object.keys(RANGE_LABELS) as ReportRangePreset[]).map((preset) => (
                <option key={preset} value={preset}>
                  {RANGE_LABELS[preset]}
                </option>
              ))}
            </select>
          </label>
        )}
        {spec.source === "activity" && spec.range.preset === "custom" && (
          <>
            <label className="field report-field">
              <span className="field-label">From</span>
              <input
                className="input"
                type="date"
                value={spec.range.from ?? ""}
                onChange={(event) =>
                  onChange({ ...spec, range: { ...spec.range, from: event.target.value } })
                }
              />
            </label>
            <label className="field report-field">
              <span className="field-label">To</span>
              <input
                className="input"
                type="date"
                value={spec.range.to ?? ""}
                onChange={(event) => onChange({ ...spec, range: { ...spec.range, to: event.target.value } })}
              />
            </label>
          </>
        )}
      </div>

      <div className="chip-bar" role="group" aria-label="Totals to show">
        <span className="report-bar-label">Show</span>
        {reportMeasures(kind).map((measure) => {
          const on = spec.measures.includes(measure);
          return (
            <button
              key={measure}
              type="button"
              className={on ? "chip chip-on" : "chip"}
              aria-pressed={on}
              // One total always stays: a table of names alone says nothing.
              disabled={on && spec.measures.length === 1}
              onClick={() =>
                onChange({
                  ...spec,
                  measures: on
                    ? spec.measures.filter((one) => one !== measure)
                    : REPORT_MEASURES.filter((one) => one === measure || spec.measures.includes(one)),
                })
              }
            >
              {measure === "cost" && <Icon name="lock" size={12} />}
              {measureLabel(measure, spec.source)}
            </button>
          );
        })}
      </div>

      <div className="filter-bar" role="group" aria-label="Report filters">
        {filters.map((dim) => {
          const options = optionsFor(dim);
          const value = spec.filters[dim];
          if (options.length < 2 && value === undefined) return null;
          return (
            <FilterChip
              key={dim}
              label={DIMENSION_LABELS[dim]}
              value={value}
              options={options}
              onChange={(next) => {
                const nextFilters = { ...spec.filters };
                if (next === undefined) delete nextFilters[dim];
                else nextFilters[dim] = next;
                onChange({ ...spec, filters: nextFilters });
              }}
            />
          );
        })}
        {spec.source === "holdings" && (
          <FilterToggle
            label={kind === "stock" ? "Include retired" : "Include sold and discarded"}
            on={spec.includeGone}
            onChange={(includeGone) => onChange({ ...spec, includeGone })}
          />
        )}
        {anyFilter && (
          <button
            type="button"
            className="button button-quiet button-small"
            onClick={() => onChange({ ...spec, filters: {} })}
          >
            Clear filters
          </button>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- table -- */

/** Past this many rows the table stops drawing; the CSV still has them all. */
const SHOWN = 500;

const cell = (measure: ReportMeasure, value: number): string =>
  measure === "value" || measure === "cost" ? formatMoney(value) : formatCount(value);

function ResultTable({
  result,
  spec,
  onSort,
  onOpen,
}: {
  result: ReportResult;
  spec: ReportSpec;
  onSort: (sort: ReportSpec["sort"]) => void;
  onOpen: (itemId: string) => void;
}) {
  const sortBy = (by: ReportSpec["sort"]["by"]) =>
    onSort({
      by,
      // A total sorts biggest first the first time; pressing again flips it.
      dir: spec.sort.by === by ? (spec.sort.dir === "asc" ? "desc" : "asc") : by === "label" ? "asc" : "desc",
    });
  const arrow = (by: ReportSpec["sort"]["by"]) =>
    spec.sort.by === by ? (spec.sort.dir === "asc" ? " ↑" : " ↓") : "";
  const ariaSort = (by: ReportSpec["sort"]["by"]) =>
    spec.sort.by === by ? (spec.sort.dir === "asc" ? "ascending" : "descending") : "none";

  function download() {
    const name = `utucat-report-${todayLocal()}.csv`;
    downloadBlob(new Blob([reportCsv(result)], { type: "text/csv;charset=utf-8" }), name);
  }

  const itemLevel = result.dimensions.indexOf("item");

  return (
    <>
      {result.range && (
        <p className="muted small report-range">
          From {calendarDay(result.range.from)} to {calendarDay(result.range.to)}.
        </p>
      )}
      {result.rows.length === 0 ? (
        <p className="muted">
          Nothing matches. Try fewer filters{result.range ? " or a longer stretch of time" : ""}.
        </p>
      ) : (
        <table className="table report-table">
          <thead>
            <tr>
              {result.dimensions.map((dim, level) => (
                <th key={dim} aria-sort={level === 0 ? ariaSort("label") : undefined}>
                  {level === 0 ? (
                    <button type="button" className="link" onClick={() => sortBy("label")}>
                      {DIMENSION_LABELS[dim]}
                      {arrow("label")}
                    </button>
                  ) : (
                    DIMENSION_LABELS[dim]
                  )}
                </th>
              ))}
              {result.measures.map((measure) => (
                <th key={measure} className="cell-number" aria-sort={ariaSort(measure)}>
                  <button type="button" className="link" onClick={() => sortBy(measure)}>
                    {measureLabel(measure, result.source)}
                    {arrow(measure)}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {result.rows.slice(0, SHOWN).map((row) => (
              <tr key={row.keys.join("\u0000")}>
                {row.labels.map((label, level) => (
                  <td key={level} data-label={DIMENSION_LABELS[result.dimensions[level]!]}>
                    {level === itemLevel ? (
                      <button type="button" className="link" onClick={() => onOpen(row.keys[level]!)}>
                        {label}
                      </button>
                    ) : row.keys[level] === "" ? (
                      <span className="muted">{label}</span>
                    ) : (
                      label
                    )}
                  </td>
                ))}
                {result.measures.map((measure) => (
                  <td key={measure} className="cell-number" data-label={measureLabel(measure, result.source)}>
                    {cell(measure, row.values[measure])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="report-total">
              <th scope="row" colSpan={result.dimensions.length}>
                Total
              </th>
              {result.measures.map((measure) => (
                <td key={measure} className="cell-number" data-label={measureLabel(measure, result.source)}>
                  {cell(measure, result.total[measure])}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      )}
      <div className="panel-foot">
        <span className="muted small">
          {result.rows.length > SHOWN &&
            `Showing the first ${SHOWN} of ${formatCount(result.rows.length)} rows. `}
          {result.measures.includes("value") &&
            result.unpriced > 0 &&
            `${plural(result.unpriced, "row")} with no price ${result.unpriced === 1 ? "adds" : "add"} nothing to ${measureLabel("value", result.source)}. `}
          {result.measures.includes("rows") &&
            result.rows.reduce((sum, row) => sum + row.values.rows, 0) > result.total.rows &&
            `Something in two rows (two tags, or units in two places) counts once in the total ${measureLabel("rows", result.source).toLowerCase()}.`}
        </span>
        <button
          type="button"
          className="button button-ghost button-small"
          disabled={result.rows.length === 0}
          onClick={download}
        >
          <Icon name="download" size={16} />
          Download CSV
        </button>
      </div>
    </>
  );
}

/* ------------------------------------------------------------- insights -- */

function InsightsPanel({
  kind,
  insights,
  short,
  onOpen,
}: {
  kind: InventoryKind;
  insights: ReturnType<typeof reportInsights> | null;
  short: Reserve[];
  onOpen: (itemId: string) => void;
}) {
  if (insights === null) return <div className="skeleton-panel" />;

  const list = (lines: InsightLine[], empty: string) =>
    lines.length === 0 ? (
      <p className="muted small">{empty}</p>
    ) : (
      <ol className="insight-list">
        {lines.map((line) => (
          <li key={`${line.itemId}-${line.title}`}>
            <button type="button" className="link" onClick={() => onOpen(line.itemId)}>
              {line.title || "Untitled item"}
            </button>
            <span className="muted small">{line.detail}</span>
          </li>
        ))}
      </ol>
    );

  return (
    <Panel
      title="Worth a look"
      note={kind === "stock" ? "Read from what you hold and the last 90 days." : "Read from what you hold."}
    >
      <div className="insights">
        {kind === "stock" && (
          <>
            <section className="insight">
              <h3 className="insight-title">
                <Icon name="clock" size={16} />
                Longest on the shelf
              </h3>
              {list(insights.oldest, "Nothing on the shelf.")}
            </section>
            <section className="insight">
              <h3 className="insight-title">
                <Icon name="zap" size={16} />
                Moving fastest
              </h3>
              {list(insights.fastest, "Nothing went out in the last 90 days.")}
            </section>
            <section className="insight">
              <h3 className="insight-title">
                <Icon name="box" size={16} />
                Sitting still
              </h3>
              <p className="muted small insight-note">On the shelf 90 days and not deployed since.</p>
              {list(insights.idle, "Everything on the shelf has moved lately.")}
            </section>
            <section className="insight">
              <h3 className="insight-title">
                <Icon name="alert" size={16} />
                Reserves running short
              </h3>
              {short.length === 0 ? (
                <p className="muted small">None.</p>
              ) : (
                <ol className="insight-list">
                  {short.slice(0, 5).map((reserve) => (
                    <li key={reserve.id}>
                      <span>{reserve.clientName || "General pool"}</span>
                      <span className="muted small">
                        {reserve.count} of {reserve.minimum} · {reserve.tags.join(", ")}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          </>
        )}
        <section className="insight">
          <h3 className="insight-title">
            <Icon name="tag" size={16} />
            Not priced
          </h3>
          <p className="insight-number">{formatCount(insights.unpriced.count)}</p>
          {list(insights.unpriced.lines, "Everything has a price.")}
        </section>
        {kind === "personal" && (
          <section className="insight">
            <h3 className="insight-title">
              <Icon name="pin" size={16} />
              Not put away
            </h3>
            <p className="insight-number">{formatCount(insights.unfiled)}</p>
            <p className="muted small">
              {plural(insights.unfiled, "thing")} filed nowhere, {formatCount(insights.uncategorised)} with no
              category.
            </p>
          </section>
        )}
      </div>
    </Panel>
  );
}
